import React, { useState, useRef, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { toast } from 'react-hot-toast';
import {
  FiUser, FiMail, FiLock, FiPhone, FiChevronDown, FiSearch,
  FiMapPin, FiCrosshair, FiCheck, FiAlertCircle, FiHome, FiUploadCloud, FiEye,
} from 'react-icons/fi';
import axiosInstance from '../api/axiosInstance';
import { loginSuccess } from '../store/userSlice';
import logo from '../Assets/Logo/logo-hd.png';
import './SignupPage.css';

// Egyptian governorates — mirrors AddressModal so the address saves cleanly.
const GOVERNORATES = [
  'Gharbia', 'Alexandria', 'Aswan', 'Asyut', 'Beheira', 'Beni Suef', 'Cairo',
  'Dakahlia', 'Damietta', 'Faiyum', 'Giza', 'Ismailia', 'Kafr El Sheikh',
  'Luxor', 'Matruh', 'Minya', 'Monufia', 'New Valley', 'North Sinai',
  'Port Said', 'Qalyubia', 'Qena', 'Red Sea', 'Sharqia', 'Sohag',
  'South Sinai', 'Suez',
];

// Map a free-text region (from reverse geocoding) onto our governorate list.
const matchGovernorate = (raw = '') => {
  const s = raw.toLowerCase();
  return GOVERNORATES.find((g) => s.includes(g.toLowerCase()))
    || (s.includes('qism') || s.includes('cairo') ? 'Cairo' : '');
};

export default function SignupPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [form, setForm] = useState({
    name: '', email: '', phone: '',
    password: '', passwordConfirm: '',
    city: '', details: '', alias: 'Home',
  });
  const [coords, setCoords] = useState(null); // { lat, lng, accuracy }
  const [geoState, setGeoState] = useState('idle'); // idle | locating | located | error
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // optional prescription
  const [rx, setRx] = useState({ rSph: '', rCyl: '', rAxis: '', lSph: '', lCyl: '', lAxis: '' });
  const [rxMethod, setRxMethod] = useState(null);
  const [rxUploading, setRxUploading] = useState(false);
  const [showManual, setShowManual] = useState(false);
  const [rxDrag, setRxDrag] = useState(false);

  // governorate dropdown
  const [menuOpen, setMenuOpen] = useState(false);
  const [search, setSearch] = useState('');
  const selectRef = useRef(null);

  const filtered = useMemo(
    () => GOVERNORATES.filter((g) => g.toLowerCase().includes(search.toLowerCase())),
    [search],
  );

  useEffect(() => {
    const onDown = (e) => {
      if (selectRef.current && !selectRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setFieldErrors((fe) => (fe[name] ? { ...fe, [name]: undefined } : fe));
  };

  // Accept 01XXXXXXXXX, +201XXXXXXXXX, 201XXXXXXXXX, or 1XXXXXXXXX → normalize to 01XXXXXXXXX.
  const normalizePhone = (raw) => {
    let p = (raw || '').replace(/[\s\-()]/g, '');
    p = p.replace(/^\+?20/, '');
    if (p && !p.startsWith('0')) p = '0' + p;
    return /^01[0125]\d{8}$/.test(p) ? p : null;
  };

  // Inline field-error line
  const Err = ({ name }) => (fieldErrors[name]
    ? <p className="sl-err-msg"><FiAlertCircle size={13} /> {fieldErrors[name]}</p>
    : null);

  // ---- optional prescription ----
  const rxChange = (e) => setRx((r) => ({ ...r, [e.target.name]: e.target.value }));
  const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));
  const buildPrescription = () => {
    if (!Object.values(rx).some((v) => v !== '' && v !== null)) return null;
    return {
      rightEye: { sph: num(rx.rSph), cyl: num(rx.rCyl), axis: num(rx.rAxis) },
      leftEye:  { sph: num(rx.lSph), cyl: num(rx.lCyl), axis: num(rx.lAxis) },
      method: rxMethod || 'manual',
    };
  };
  const onRxUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('prescription', file);
    setRxUploading(true);
    try {
      const res = await axiosInstance.post('/prescription/extract', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const p = res.data.prescription || {};
      setRx({
        rSph: p.rightEye?.sph ?? '', rCyl: p.rightEye?.cyl ?? '', rAxis: p.rightEye?.axis ?? '',
        lSph: p.leftEye?.sph ?? '',  lCyl: p.leftEye?.cyl ?? '',  lAxis: p.leftEye?.axis ?? '',
      });
      setRxMethod('upload');
      toast.success('Prescription read — review the values below.');
    } catch {
      toast.error('Could not read that file. You can enter the values manually.');
    } finally {
      setRxUploading(false);
    }
  };

  // ---- progress hint (visual only) ----
  const filledIdentity = form.name && form.email && form.password.length >= 6;
  const filledLocation = form.city && form.details;
  const stepClass = (n) =>
    `sl-step${(n === 1) || (n === 2 && filledIdentity) || (n === 3 && filledLocation) ? ' active' : ''}`;

  // ---- geolocation ----
  const detectLocation = () => {
    if (!('geolocation' in navigator)) {
      toast.error('Location is not supported on this device.');
      return;
    }
    setGeoState('locating');
    navigator.geolocation.getCurrentPosition(
      async ({ coords: c }) => {
        const lat = +c.latitude.toFixed(6);
        const lng = +c.longitude.toFixed(6);
        setCoords({ lat, lng, accuracy: Math.round(c.accuracy) });

        // Best-effort reverse geocode (OpenStreetMap Nominatim — free, no key).
        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&accept-language=en`,
            { headers: { Accept: 'application/json' } },
          );
          if (res.ok) {
            const data = await res.json();
            const a = data.address || {};
            const gov = matchGovernorate(
              [a.state, a.county, a.region, a.city, data.display_name].filter(Boolean).join(' '),
            );
            const street = [a.road, a.house_number, a.neighbourhood, a.suburb]
              .filter(Boolean).join(', ');
            setForm((f) => ({
              ...f,
              city: gov || f.city,
              details: f.details || street || data.display_name || '',
            }));
            toast.success('Location detected — review the details below.');
          } else {
            toast.success('Coordinates captured. Please pick your governorate.');
          }
        } catch {
          toast.success('Coordinates captured. Please pick your governorate.');
        }
        setGeoState('located');
      },
      (err) => {
        setGeoState('error');
        toast.error(
          err.code === err.PERMISSION_DENIED
            ? 'Location permission denied. You can enter your address manually.'
            : 'Could not get your location. Please enter it manually.',
        );
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 0 },
    );
  };

  // ---- submit ----
  const submit = async (e) => {
    e.preventDefault();
    setError('');

    const phone = normalizePhone(form.phone);
    const errs = {};
    if (form.name.trim().length < 3) errs.name = 'Please enter your full name (at least 3 characters).';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) errs.email = 'Please enter a valid email address.';
    if (form.password.length < 6) errs.password = 'At least 6 characters.';
    if (form.password !== form.passwordConfirm) errs.passwordConfirm = 'Passwords do not match.';
    if (!phone) errs.phone = 'Enter a valid Egyptian mobile (e.g., 01012345678 or +201012345678).';
    if (!form.city) errs.city = 'Please select your governorate.';
    if (!form.details.trim()) errs.details = 'Please add your specific address / location.';

    if (Object.keys(errs).length) {
      setFieldErrors(errs);
      const first = Object.keys(errs)[0];
      toast.error(errs[first]);
      const el = document.getElementById(first);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      if (el && typeof el.focus === 'function') setTimeout(() => el.focus(), 300);
      return;
    }
    setFieldErrors({});

    const prescription = buildPrescription();

    setSubmitting(true);
    try {
      // 1) Create the account
      const signupRes = await axiosInstance.post('/auth/signup', {
        name: form.name.trim(),
        email: form.email.trim(),
        phone,
        password: form.password,
        passwordConfirm: form.passwordConfirm,
        ...(prescription ? { prescription } : {}),
      });

      const token = signupRes.data.token;
      if (!token) throw new Error('No token returned from signup.');
      localStorage.setItem('token', token);

      // 2) Hydrate the user
      const me = await axiosInstance.get('/users/getMe');
      const user = {
        ...me.data.data,
        addresses: me.data.data.addresses || [],
        orders: me.data.data.orders || [],
      };
      dispatch(loginSuccess({ token, user }));

      // 3) Save the address with precise GPS coordinates (lat/lng) when available.
      try {
        const addrRes = await axiosInstance.post('/addresses', {
          alias: form.alias.trim() || 'Home',
          details: form.details.trim(),
          phone,
          city: form.city.trim(),
          postalCode: '00000',
          ...(coords ? { lat: coords.lat, lng: coords.lng } : {}),
        });
        if (addrRes.data?.data) {
          dispatch({ type: 'user/updateUserInfo', payload: { addresses: addrRes.data.data } });
        }
      } catch {
        // Account is created; address can be retried from the profile.
        toast('Account created — you can confirm your address in your profile.', { icon: 'ℹ️' });
      }

      toast.success(`Welcome to Selection Lenses, ${user.name?.split(' ')[0] || ''}!`);
      navigate('/');
    } catch (err) {
      const msg = err.response?.data?.message
        || err.response?.data?.errors?.[0]?.msg
        || 'Sign up failed. Please check your details and try again.';
      setError(msg);
      toast.error(msg);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="sl-signup">
      {/* ---------------- LEFT: brand atmosphere ---------------- */}
      <aside className="sl-brand">
        <div className="sl-iris" aria-hidden="true" />

        <div className="sl-brand-top">
          <div className="sl-logo-badge">
            <img src={logo} alt="Selection Lenses" />
          </div>
        </div>

        <div className="sl-brand-mid">
          <div className="sl-eyebrow sl-reveal sl-d1">Create your account</div>
          <h1 className="sl-headline sl-reveal sl-d2">
            See the<br /><em>difference</em><br />in detail.
          </h1>
          <p className="sl-sub sl-reveal sl-d3">
            Colored &amp; clear lenses, accessories, and prescription tools —
            delivered to your exact doorstep across Egypt.
          </p>
        </div>

        <div className="sl-brand-bottom sl-reveal sl-d4">
          <div className="sl-quote-line"><span className="dot" /> 470+ products from trusted brands</div>
          <div className="sl-quote-line"><span className="dot" /> Pinpoint delivery with live location</div>
          <div className="sl-quote-line"><span className="dot" /> Free eye-power finder built in</div>
        </div>
      </aside>

      {/* ---------------- RIGHT: form ---------------- */}
      <main className="sl-form-wrap">
        <form className="sl-form" onSubmit={submit} noValidate>
          <div className="sl-form-head sl-reveal sl-d1">
            <img src={logo} alt="Selection Lenses" className="sl-logo" />
            <h2 className="sl-title">Join the <em>focus</em>.</h2>
            <p className="sl-title-sub">
              Already a member? <Link to="/login">Sign in</Link>
            </p>
          </div>

          <div className="sl-steps sl-reveal sl-d1">
            <span className={stepClass(1)} />
            <span className={stepClass(2)} />
            <span className={stepClass(3)} />
          </div>

          {error && (
            <div className="sl-error sl-reveal">
              <FiAlertCircle style={{ marginTop: 2, flex: 'none' }} />
              <span>{error}</span>
            </div>
          )}

          {/* identity */}
          <div className="sl-field sl-reveal sl-d2">
            <label className="sl-label" htmlFor="name">Full name <span className="req">*</span></label>
            <div className="sl-input-icon">
              <FiUser className="ic" />
              <input id="name" name="name" className={`sl-input ${fieldErrors.name ? 'err' : ''}`} autoComplete="name"
                placeholder="e.g., Mariam Soliman" value={form.name} onChange={change} required />
            </div>
            <Err name="name" />
          </div>

          <div className="sl-field sl-reveal sl-d2">
            <label className="sl-label" htmlFor="email">Email address <span className="req">*</span></label>
            <div className="sl-input-icon">
              <FiMail className="ic" />
              <input id="email" name="email" type="email" className={`sl-input ${fieldErrors.email ? 'err' : ''}`} autoComplete="email"
                placeholder="you@example.com" value={form.email} onChange={change} required />
            </div>
            <Err name="email" />
          </div>

          <div className="sl-row sl-reveal sl-d3">
            <div className="sl-field">
              <label className="sl-label" htmlFor="password">Password <span className="req">*</span></label>
              <div className="sl-input-icon">
                <FiLock className="ic" />
                <input id="password" name="password" type="password" className={`sl-input ${fieldErrors.password ? 'err' : ''}`}
                  autoComplete="new-password" placeholder="Min. 6 characters"
                  value={form.password} onChange={change} required />
              </div>
              <Err name="password" />
            </div>
            <div className="sl-field">
              <label className="sl-label" htmlFor="passwordConfirm">Confirm <span className="req">*</span></label>
              <div className="sl-input-icon">
                <FiLock className="ic" />
                <input id="passwordConfirm" name="passwordConfirm" type="password" className={`sl-input ${fieldErrors.passwordConfirm ? 'err' : ''}`}
                  autoComplete="new-password" placeholder="Repeat password"
                  value={form.passwordConfirm} onChange={change} required />
              </div>
              <Err name="passwordConfirm" />
            </div>
          </div>

          <div className="sl-field sl-reveal sl-d3">
            <label className="sl-label" htmlFor="phone">Phone number <span className="req">*</span></label>
            <div className="sl-input-icon">
              <FiPhone className="ic" />
              <input id="phone" name="phone" type="tel" className={`sl-input ${fieldErrors.phone ? 'err' : ''}`} autoComplete="tel"
                placeholder="01012345678 or +201012345678" value={form.phone} onChange={change} required />
            </div>
            <Err name="phone" />
          </div>

          {/* location detector */}
          <div className={`sl-geo sl-reveal sl-d4 ${geoState}`}>
            <div className="sl-geo-head">
              <div className="sl-geo-radar">
                <FiMapPin className="pin" />
              </div>
              <div className="sl-geo-text">
                <div className="sl-geo-title">
                  {geoState === 'located' ? 'Location pinned' : 'Use my current location'}
                </div>
                <div className="sl-geo-desc">
                  {geoState === 'locating' ? 'Finding you…'
                    : geoState === 'located' ? 'We pre-filled your address — edit if needed.'
                    : 'Auto-fill your governorate & address with one tap.'}
                </div>
              </div>
              <button type="button" className="sl-geo-btn" onClick={detectLocation}
                disabled={geoState === 'locating'}>
                <FiCrosshair />
                {geoState === 'located' ? 'Re-detect' : 'Detect'}
              </button>
            </div>

            {coords && (
              <div className="sl-geo-chip">
                <FiCheck style={{ color: 'var(--ok)' }} />
                <span className="sl-coord"><b>LAT</b> {coords.lat} &nbsp; <b>LNG</b> {coords.lng}</span>
                <span className="sl-acc">±{coords.accuracy}m</span>
                <a className="sl-maplink" target="_blank" rel="noreferrer"
                  href={`https://maps.google.com/?q=${coords.lat},${coords.lng}`}>
                  View on map →
                </a>
              </div>
            )}
          </div>

          {/* governorate */}
          <div className="sl-field sl-reveal sl-d5">
            <label className="sl-label">Governorate / City <span className="req">*</span></label>
            <div className="sl-select" ref={selectRef}>
              <button type="button" id="city" className={`sl-select-btn ${menuOpen ? 'open' : ''} ${fieldErrors.city ? 'err' : ''}`}
                onClick={() => setMenuOpen((o) => !o)}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.55rem' }}>
                  <FiMapPin style={{ color: 'var(--muted)' }} />
                  {form.city
                    ? <span>{form.city}</span>
                    : <span className="placeholder">Select your governorate</span>}
                </span>
                <FiChevronDown className="chev" />
              </button>

              {menuOpen && (
                <div className="sl-menu">
                  <div className="sl-menu-search">
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <FiSearch style={{ color: 'var(--muted)' }} />
                      <input autoFocus value={search} onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search governorate…" />
                    </div>
                  </div>
                  {filtered.length ? filtered.map((g) => (
                    <div key={g} className={`sl-menu-item ${form.city === g ? 'hl' : ''}`}
                      onClick={() => { setForm((f) => ({ ...f, city: g })); setFieldErrors((fe) => ({ ...fe, city: undefined })); setMenuOpen(false); setSearch(''); }}>
                      {g}
                    </div>
                  )) : <div className="sl-menu-empty">No matches</div>}
                </div>
              )}
            </div>
            <Err name="city" />
          </div>

          {/* specific location */}
          <div className="sl-field sl-reveal sl-d5">
            <label className="sl-label" htmlFor="details">Specific address / location <span className="req">*</span></label>
            <textarea id="details" name="details" className={`sl-textarea ${fieldErrors.details ? 'err' : ''}`}
              placeholder="Street, building, floor, apartment, nearby landmark…"
              value={form.details} onChange={change} required />
            <Err name="details" />
          </div>

          {/* alias */}
          <div className="sl-field sl-reveal sl-d5">
            <label className="sl-label" htmlFor="alias">Save address as</label>
            <div className="sl-input-icon">
              <FiHome className="ic" />
              <input id="alias" name="alias" className="sl-input"
                placeholder="Home, Work…" value={form.alias} onChange={change} />
            </div>
          </div>

          {/* optional prescription */}
          <div className="sl-field sl-reveal sl-d5">
            <label className="sl-label">
              <FiEye style={{ verticalAlign: '-2px', marginRight: 4 }} />
              Prescription <span className="sl-optional">optional</span>
            </label>

            {/* Primary: prominent AI upload dropzone */}
            <label
              className={`sl-rx-drop ${rxDrag ? 'drag' : ''} ${rxUploading ? 'busy' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setRxDrag(true); }}
              onDragLeave={() => setRxDrag(false)}
              onDrop={(e) => { e.preventDefault(); setRxDrag(false); onRxUpload(e.dataTransfer.files[0]); }}
            >
              <input type="file" accept="image/*,application/pdf" hidden disabled={rxUploading}
                onChange={(e) => onRxUpload(e.target.files[0])} />
              <div className="sl-rx-drop-icon">
                {rxUploading ? <span className="sl-spinner" /> : <FiUploadCloud />}
              </div>
              <div className="sl-rx-drop-text">
                <div className="sl-rx-drop-title">
                  {rxUploading ? 'Reading your prescription…' : 'Upload your prescription'}
                  {!rxUploading && <span className="sl-rx-ai">AI</span>}
                </div>
                <div className="sl-rx-drop-sub">
                  {rxUploading
                    ? 'Extracting SPH · CYL · Axis automatically'
                    : 'Drop a photo or PDF — we read the values for you'}
                </div>
              </div>
              {rxMethod === 'upload' && !rxUploading && (
                <FiCheck className="sl-rx-drop-ok" />
              )}
            </label>

            {/* Review / manual entry — appears after upload or when chosen */}
            {(showManual || rxMethod === 'upload') ? (
              <div className="sl-rx">
                {rxMethod === 'upload' && (
                  <div className="sl-rx-divider">review &amp; edit the detected values</div>
                )}
                <div className="sl-rx-row">
                  <span className="sl-rx-eye">OD</span>
                  <input name="rSph" value={rx.rSph} onChange={rxChange} className="sl-input sl-rx-in" placeholder="SPH" inputMode="decimal" />
                  <input name="rCyl" value={rx.rCyl} onChange={rxChange} className="sl-input sl-rx-in" placeholder="CYL" inputMode="decimal" />
                  <input name="rAxis" value={rx.rAxis} onChange={rxChange} className="sl-input sl-rx-in" placeholder="Axis" inputMode="numeric" />
                </div>
                <div className="sl-rx-row">
                  <span className="sl-rx-eye">OS</span>
                  <input name="lSph" value={rx.lSph} onChange={rxChange} className="sl-input sl-rx-in" placeholder="SPH" inputMode="decimal" />
                  <input name="lCyl" value={rx.lCyl} onChange={rxChange} className="sl-input sl-rx-in" placeholder="CYL" inputMode="decimal" />
                  <input name="lAxis" value={rx.lAxis} onChange={rxChange} className="sl-input sl-rx-in" placeholder="Axis" inputMode="numeric" />
                </div>
              </div>
            ) : (
              <button type="button" className="sl-rx-manual-link" onClick={() => setShowManual(true)}>
                Prefer to type it? Enter manually
              </button>
            )}
          </div>

          <button type="submit" className="sl-submit sl-reveal sl-d6" disabled={submitting}>
            {submitting && <span className="sl-spinner" />}
            <span>{submitting ? 'Creating your account…' : 'Create account'}</span>
          </button>

          <p className="sl-foot sl-reveal sl-d6">
            By creating an account you agree to our terms of service.<br />
            Your location is used only to speed up delivery.
          </p>
        </form>
      </main>
    </div>
  );
}
